import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { blockchainService } from '../services/blockchainService';
import { TOKEN_SYMBOL, TOKEN_DECIMALS } from '../config/token';

interface Wallet {
  id: string;
  publicKey: string;
  type: string;
  balance: string;
  isHotWallet?: boolean;
}

interface WalletOption {
  id: string;
  publicKey: string;
  type: string;
  balance: string;
  displayName: string;
  isHotWallet?: boolean;
}

const AMOUNT_STEP = Math.pow(10, -TOKEN_DECIMALS).toFixed(TOKEN_DECIMALS);

const Transfer: React.FC = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [hotWallet, setHotWallet] = useState<Wallet | null>(null);
  const [allWalletOptions, setAllWalletOptions] = useState<WalletOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [formData, setFormData] = useState({
    fromWalletId: '',
    toWalletId: '',
    amount: '',
    metadata: '',
  });

  useEffect(() => {
    fetchWallets();
    fetchHotWallet();
  }, []);

  useEffect(() => {
    const options: WalletOption[] = [];

    if (hotWallet) {
      options.push({
        id: hotWallet.id,
        publicKey: hotWallet.publicKey,
        type: 'HOT',
        balance: hotWallet.balance,
        displayName: `🔥 Hot Wallet - ${hotWallet.publicKey.substring(0, 8)}...${hotWallet.publicKey.slice(-8)} - ${parseFloat(hotWallet.balance)} ${TOKEN_SYMBOL}`,
        isHotWallet: true,
      });
    }

    wallets.forEach((wallet) => {
      options.push({
        id: wallet.id,
        publicKey: wallet.publicKey,
        type: wallet.type,
        balance: wallet.balance,
        displayName: `${wallet.type} - ${wallet.publicKey.substring(0, 8)}...${wallet.publicKey.slice(-8)} - ${parseFloat(wallet.balance)} ${TOKEN_SYMBOL}`,
        isHotWallet: false,
      });
    });

    setAllWalletOptions(options);
  }, [wallets, hotWallet]);

  const fetchWallets = async () => {
    try {
      const response = await blockchainService.getWallets();
      setWallets(response);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  const fetchHotWallet = async () => {
    try {
      const walletsResponse = await blockchainService.getWallets();
      const hotWalletFromList = walletsResponse.find((w) => w.type === 'HOT');

      if (hotWalletFromList) {
        setHotWallet(hotWalletFromList);
      } else {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
        await axios.post(`${API_BASE_URL}/wallet/initialize-hot-wallet`);
        const response = await axios.get(`${API_BASE_URL}/wallet/hot`);
        setHotWallet(response.data);
      }
    } catch (error) {
      console.error('Error fetching hot wallet:', error);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
        const response = await axios.get(`${API_BASE_URL}/wallet/hot`);
        setHotWallet(response.data);
      } catch (fallbackError) {
        console.error('Fallback hot wallet fetch failed:', fallbackError);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const fromWallet = allWalletOptions.find((w) => w.id === formData.fromWalletId);
      if (!fromWallet) {
        throw new Error('From wallet not found');
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      const fromBalance = parseFloat(fromWallet.balance);
      if (fromBalance < amount) {
        throw new Error(`Insufficient balance. Available: ${fromBalance} ${TOKEN_SYMBOL}`);
      }

      if (formData.metadata.trim()) {
        try {
          JSON.parse(formData.metadata);
        } catch (parseError) {
          throw new Error('Invalid JSON metadata');
        }
      }

      const transferRequest = {
        vin: {
          from: fromWallet.publicKey,
          to: formData.toWalletId,
        },
        transaction_index: `TXN_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        quantity: amount,
        memo: formData.metadata || `Transfer of ${amount} ${TOKEN_SYMBOL} tokens`,
        callback: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/callback/receive`,
      };

      const response = await blockchainService.transfer(transferRequest);

      setSuccess(`Transfer initiated successfully! ${response.message}`);

      setFormData({
        fromWalletId: '',
        toWalletId: '',
        amount: '',
        metadata: '',
      });
      setFromSearchTerm('');
      setToSearchTerm('');

      await fetchWallets();
      await fetchHotWallet();
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const filterWallets = (searchTerm: string, excludeId?: string) => {
    if (!searchTerm) return allWalletOptions.filter((w) => w.id !== excludeId);

    return allWalletOptions.filter(
      (wallet) =>
        wallet.id !== excludeId &&
        (wallet.publicKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
          wallet.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          wallet.displayName.toLowerCase().includes(searchTerm.toLowerCase())),
    );
  };

  const selectFromWallet = (wallet: WalletOption) => {
    setFormData({ ...formData, fromWalletId: wallet.id });
    setFromSearchTerm(wallet.displayName);
    setShowFromDropdown(false);
  };

  const selectToWallet = (wallet: WalletOption) => {
    setFormData({ ...formData, toWalletId: wallet.publicKey });
    setToSearchTerm(wallet.displayName);
    setShowToDropdown(false);
  };

  const handleFromSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromSearchTerm(e.target.value);
    setShowFromDropdown(true);

    if (e.target.value.length >= 32) {
      const wallet = allWalletOptions.find((w) => w.publicKey === e.target.value);
      if (wallet) {
        setFormData({ ...formData, fromWalletId: wallet.id });
      }
    }
  };

  const handleToSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToSearchTerm(e.target.value);
    setShowToDropdown(true);

    if (e.target.value.length >= 32) {
      setFormData({ ...formData, toWalletId: e.target.value });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const searchInputClass =
    'mt-1 block w-full pl-3 pr-10 py-2 text-base border-slate-600 bg-slate-700 text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md';
  const labelClass = 'block text-sm font-medium text-slate-300';

  const renderDropdown = (searchTerm: string, excludeId: string | undefined, onSelect: (wallet: WalletOption) => void) => (
    <div className="absolute z-10 mt-1 w-full bg-slate-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-slate-700 overflow-auto focus:outline-none sm:text-sm">
      {filterWallets(searchTerm, excludeId).map((wallet) => (
        <div
          key={wallet.id}
          onClick={() => onSelect(wallet)}
          className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-slate-200 hover:bg-indigo-600 hover:text-white"
        >
          <div className="flex items-center">
            <span className="font-normal block truncate">{wallet.displayName}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(wallet.publicKey);
              }}
              className="ml-2 p-1 hover:bg-slate-700 rounded"
              title="Copy address"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-slate-100">Transfer Tokens</h2>
          <p className="mt-1 text-sm text-slate-400">
            Transfer tokens from a customer wallet to a merchant wallet
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* From Wallet */}
          <div className="relative">
            <label htmlFor="fromWalletId" className={labelClass}>
              From Wallet
            </label>
            <div className="relative">
              <input
                type="text"
                value={fromSearchTerm}
                onChange={handleFromSearchChange}
                onFocus={() => setShowFromDropdown(true)}
                onBlur={() => setTimeout(() => setShowFromDropdown(false), 200)}
                placeholder="Search wallets or paste address..."
                className={searchInputClass}
                required
              />
              <button
                type="button"
                onClick={() => setShowFromDropdown(!showFromDropdown)}
                className="absolute inset-y-0 right-0 flex items-center pr-2"
              >
                <ChevronDownIcon className="h-5 w-5 text-slate-400" />
              </button>
            </div>

            {showFromDropdown && renderDropdown(fromSearchTerm, formData.toWalletId, selectFromWallet)}
          </div>

          {/* To Wallet */}
          <div className="relative">
            <label htmlFor="toWalletId" className={labelClass}>
              To Wallet
            </label>
            <div className="relative">
              <input
                type="text"
                value={toSearchTerm}
                onChange={handleToSearchChange}
                onFocus={() => setShowToDropdown(true)}
                onBlur={() => setTimeout(() => setShowToDropdown(false), 200)}
                placeholder="Search wallets or paste address..."
                className={searchInputClass}
                required
              />
              <button
                type="button"
                onClick={() => setShowToDropdown(!showToDropdown)}
                className="absolute inset-y-0 right-0 flex items-center pr-2"
              >
                <ChevronDownIcon className="h-5 w-5 text-slate-400" />
              </button>
            </div>

            {showToDropdown && renderDropdown(toSearchTerm, formData.fromWalletId, selectToWallet)}
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className={labelClass}>
              Amount ({TOKEN_SYMBOL})
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              required
              min="0"
              step={AMOUNT_STEP}
              className="mt-1 block w-full border-slate-600 bg-slate-700 text-slate-100 placeholder-slate-400 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="0.00"
            />
          </div>

          {/* Metadata */}
          <div>
            <div className="flex justify-between items-center">
              <label htmlFor="metadata" className={labelClass}>
                Metadata (JSON - Optional)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, metadata: JSON.stringify({
                    type: 'payment',
                    orderId: 'ORD-' + Date.now(),
                    category: 'services',
                    description: 'Web development services',
                  }, null, 2) })}
                  className="text-xs px-2 py-1 bg-blue-500/15 text-blue-300 rounded hover:bg-blue-500/25"
                >
                  Payment Sample
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, metadata: JSON.stringify({
                    type: 'refund',
                    originalOrderId: 'ORD-12345',
                    reason: 'Customer request',
                    refundDate: new Date().toISOString(),
                  }, null, 2) })}
                  className="text-xs px-2 py-1 bg-green-500/15 text-green-300 rounded hover:bg-green-500/25"
                >
                  Refund Sample
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, metadata: JSON.stringify({
                    type: 'reward',
                    userId: 'USER-' + Date.now(),
                    points: 100,
                    campaign: 'referral_bonus',
                  }, null, 2) })}
                  className="text-xs px-2 py-1 bg-purple-500/15 text-purple-300 rounded hover:bg-purple-500/25"
                >
                  Reward Sample
                </button>
              </div>
            </div>
            <textarea
              id="metadata"
              name="metadata"
              value={formData.metadata}
              onChange={handleInputChange}
              rows={6}
              className="mt-1 block w-full border-slate-600 bg-slate-700 text-slate-100 placeholder-slate-400 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono text-xs"
              placeholder='{"transaction_id":"abc123","type":"transfer"}'
            />
            <p className="mt-1 text-xs text-slate-400">
              Optional JSON metadata - Use sample buttons above for quick templates
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-300">Transfer failed</h3>
                  <div className="mt-2 text-sm text-red-300">{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="rounded-md border border-green-500/30 bg-green-500/10 p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-300">Transfer successful</h3>
                  <div className="mt-2 text-sm text-green-300">{success}</div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/transactions')}
              className="px-4 py-2 border border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Transfer Tokens
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Transfer;
